import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { ArrowLeftSvg } from '../../../../assets/icons';
import theme from '../../../../assets/theme';
import { TabState, useDesignerUIContext } from '../../../../hooks/useDesignerUIState';
import { useMetrics } from '../../../../hooks/useMetrics';
import useMixpanelFlag from '../../../../hooks/useMixpanelFlag';
import { usePrevious } from '../../../../hooks/usePrevious';
import { TenorGIF } from '../../../../hooks/useTenorGIFs';
import { Button } from '../../../_atoms/Button';
import { IconWrapper } from '../../../_atoms/IconWrapper';
import {
  OnElementsChangeProps,
  Slide,
  SlideElement,
} from '../../../SlideComponents/slide.types';
import { createImageElement } from '../../../SlideComponents/slideUtil';
import { TabContainer } from '../../DesignerTabs/DesignerTabUtils';
import AppSmashPicker from '../BackgroundTab/AppSmashPicker';
import AppSmashRow from '../BackgroundTab/AppSmashRow';
import { appSmashes, IframeAppSmash } from '../BackgroundTab/AppSmashUtils';
import GiphyPicker from '../BackgroundTab/GiphyPicker';
import GiphyRow from '../BackgroundTab/GiphyRow';
import TenorGIFPicker from '../BackgroundTab/TenorGIFPicker';
import { GIFRow } from '../BackgroundTab/TenorGIFRow';
import UploadFilePicker from '../BackgroundTab/UploadFilePicker';

const MediaRootTab = ({
  onRoundChange,
  round,
  templateId,
  droppedImageData,
  onDroppedImageDataConsumed,
  onElementsChange,
  selectedElements,
  tabState,
}: {
  onRoundChange: (round: Slide) => void;
  round?: Slide;
  templateId: string;
  droppedImageData?: { src: string; mimeType: string };
  onDroppedImageDataConsumed: () => void;
  selectedElements: SlideElement[] | null;
  onElementsChange: (prop: OnElementsChangeProps) => void;
  tabState: TabState;
}) => {
  const { t } = useTranslation('Host');
  const panelStart: PaneScreen = useMemo(() => {
    if (selectedElements?.length !== 1) return 'default';
    const selectedElement = selectedElements?.[0];
    if (!selectedElement) return 'default';
    if (
      selectedElement.type === 'iframe' &&
      selectedElement.iframeOrigin &&
      tabState.subTab === 'appSmash'
    ) {
      return 'iframe';
    }

    return 'default';
  }, [selectedElements, tabState.subTab]);
  if (round) {
    return (
      <MediaTab
        templateId={templateId}
        onRoundChange={onRoundChange}
        slide={round}
        tabState={tabState}
        panelStart={panelStart}
        droppedImageData={droppedImageData}
        onElementsChange={onElementsChange}
        selectedElements={selectedElements}
        onDroppedImageDataConsumed={onDroppedImageDataConsumed}
      />
    );
  } else {
    return (
      <TabContainer style={{ paddingTop: 20 }}>
        <Title style={{ marginBottom: 10 }} $fontSize={20}>
          {t('Media')}
        </Title>
        <h4>{t('Select a slide or another tab to get started')}</h4>
      </TabContainer>
    );
  }
};

type PaneScreen = 'default' | 'upload' | 'giphy' | 'tenorgifs' | 'iframe';

type MediaPaneState = {
  screen: PaneScreen;
  screenData?: { iframeType: IframeAppSmash };
};

function isSlidesBackgroundDrawingImageElement(
  slide: Slide,
  selectedElement: SlideElement | null,
) {
  if (slide.type !== 'canvas') return false;
  if (!selectedElement) return false;
  const isDrawingContextElement = slide.contextElements.some(
    e => e.elementId === selectedElement?.id && e.purpose === 'drawingBackground',
  );
  return isDrawingContextElement;
}

const MediaTab = ({
  onRoundChange,
  slide,
  droppedImageData,
  selectedElements,
  onDroppedImageDataConsumed,
  onElementsChange,
  panelStart,
  tabState,
}: {
  onRoundChange: (slide: Slide) => void;
  slide: Slide;
  selectedElements: SlideElement[] | null;
  templateId: string;
  droppedImageData?: { src: string; mimeType: string };
  onDroppedImageDataConsumed: () => void;
  onElementsChange: (prop: OnElementsChangeProps) => void;
  panelStart: PaneScreen;
  tabState: TabState;
}) => {
  const { t } = useTranslation('Host');
  const setSelectedElements = useDesignerUIContext(
    context => context.setSelectedElements,
  );
  /* Main state */
  const [paneScreen, setPaneScreen] = useState<MediaPaneState>({ screen: panelStart });
  const metrics = useMetrics();

  const isTenorGIFsEnabled = useMixpanelFlag('tenor_gifs');

  // we only make edits to single elements at a time
  const selectedElement = useMemo(() => {
    if (selectedElements?.length !== 1) return null;
    return selectedElements[0];
  }, [selectedElements]);
  /* Util state */
  const previousDroppedImageData = usePrevious(droppedImageData);
  const previousSelectedElement = usePrevious(selectedElement);
  const [localParentDroppedImageData, setLocalParentDroppedImageData] = useState<
    { src: string; mimeType: string } | undefined
  >();
  const currentRoundHasCroppableMimetype = useMemo(() => {
    if (!selectedElement) return false;
    if (isSlidesBackgroundDrawingImageElement(slide, selectedElement)) {
      // This is the background drawing image - we don't want to crop it
      return false;
    }
    if (selectedElement.type === 'image') {
      if (
        selectedElement.mediaMimetype === 'image/png' ||
        selectedElement.mediaMimetype === 'image/jpeg' ||
        selectedElement.mediaMimetype === 'image/heic' ||
        selectedElement.mediaMimetype === 'image/webp'
      )
        return true;
    }
  }, [selectedElement, slide]);

  useEffect(() => {
    // When we detect droppedImageData suddenly change
    // we tell parent component the initial data has been consumed
    // and we store that initial data locally in the component
    if (
      previousDroppedImageData === undefined &&
      droppedImageData &&
      !isSlidesBackgroundDrawingImageElement(slide, selectedElement)
    ) {
      setLocalParentDroppedImageData(droppedImageData);
      onDroppedImageDataConsumed();
    }
  }, [
    droppedImageData,
    previousDroppedImageData,
    onDroppedImageDataConsumed,
    slide,
    selectedElement,
  ]);
  useEffect(() => {
    if (tabState.subTab === 'appSmash' && selectedElement?.iframeOrigin) {
      setPaneScreen({
        screen: 'iframe',
        screenData: { iframeType: selectedElement.iframeOrigin },
      });
    }
  }, [tabState.subTab, selectedElement]);

  useEffect(() => {
    // Upon consuming a parent dropped image - and we are not on the crop
    // screen - we move into the crop screen
    if (
      localParentDroppedImageData &&
      paneScreen.screen !== 'upload' &&
      !isSlidesBackgroundDrawingImageElement(slide, selectedElement)
    ) {
      setPaneScreen({ screen: 'upload' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localParentDroppedImageData, paneScreen, selectedElement]);

  const handleCancelUpload = useCallback(() => {
    // Make sure to set localdropped data to undefined whenever we exit the upload screen
    setLocalParentDroppedImageData(undefined);
    setPaneScreen({ screen: 'default' });
  }, []);

  useEffect(() => {
    /* Upon deselecting a selected media element - cancel cropping  */
    if (!!previousSelectedElement && !selectedElement && paneScreen.screen === 'upload') {
      handleCancelUpload();
    }
  }, [selectedElement, previousSelectedElement, paneScreen, handleCancelUpload]);

  const handleSelectPaneScreen = (state: MediaPaneState) => {
    setPaneScreen(state);
  };

  const handleGifSelected = (gifId: string, width: number, height: number) => {
    metrics.logEvent('Creator.MediaTab.Add', { mimeType: 'gif' });
    const newImageElement: SlideElement = createImageElement({
      url: `https://media0.giphy.com/media/${gifId}/giphy.gif`,
      mediaMimeType: 'giphy/gif',
      width,
      height,
    });
    onRoundChange({
      ...slide,
      elements: [...slide.elements, newImageElement],
    });
    setSelectedElements([newImageElement]);
  };

  const handleTenorGifSelected = (gif: TenorGIF) => {
    metrics.logEvent('Creator.MediaTab.Add', { mimeType: 'gif' });
    const newImageElement: SlideElement = createImageElement({
      url: gif.media_formats.gif.url,
      mediaMimeType: 'tenor/gif',
      width: gif.media_formats.gif.dims[0],
      height: gif.media_formats.gif.dims[1],
    });
    onRoundChange({
      ...slide,
      elements: [...slide.elements, newImageElement],
    });
    setSelectedElements([newImageElement]);
  };

  return (
    <div style={{ paddingTop: 20, height: '100%', position: 'relative' }}>
      {paneScreen.screen === 'default' && (
        <OverviewSelectorContainer>
          <Title style={{ marginBottom: 10 }} $fontSize={20}>
            {selectedElement ? t('Edit media') : t('Add Media')}
          </Title>
          <BackgroundTabContainer>
            <Button
              styles={{ background: theme.colors.blue }}
              onClick={() => {
                handleSelectPaneScreen({ screen: 'upload' });
              }}
            >
              {currentRoundHasCroppableMimetype ? t('Edit') : t('Upload files')}
            </Button>

            {!isTenorGIFsEnabled && (
              <GiphyRow
                onClickSeeMore={() => handleSelectPaneScreen({ screen: 'giphy' })}
                onGifSelected={handleGifSelected}
              />
            )}

            {isTenorGIFsEnabled && (
              <GIFRow
                onClickSeeMore={() => handleSelectPaneScreen({ screen: 'tenorgifs' })}
                onGifSelected={handleTenorGifSelected}
              />
            )}

            <AppSmashRow
              onIframeIntegrationSelected={appSmash =>
                handleSelectPaneScreen({
                  screen: 'iframe',
                  screenData: { iframeType: appSmash },
                })
              }
            />
          </BackgroundTabContainer>
        </OverviewSelectorContainer>
      )}
      {paneScreen.screen === 'giphy' && (
        <>
          <BackButtonRowContainer
            onClick={() => handleSelectPaneScreen({ screen: 'default' })}
          >
            <IconWrapper height="30px" width="30px" color={theme.colors.black}>
              <ArrowLeftSvg />
            </IconWrapper>
            <Title $fontSize={20}>Giphy</Title>
          </BackButtonRowContainer>
          <ContentContainer style={{ height: 'calc(100% - 40px)' }}>
            <GiphyPicker onGifSelected={handleGifSelected} />
          </ContentContainer>
        </>
      )}
      {paneScreen.screen === 'tenorgifs' && (
        <>
          <BackButtonRowContainer
            onClick={() => handleSelectPaneScreen({ screen: 'default' })}
          >
            <IconWrapper height="30px" width="30px" color={theme.colors.black}>
              <ArrowLeftSvg />
            </IconWrapper>
            <Title $fontSize={20}>GIFs</Title>
          </BackButtonRowContainer>
          <ContentContainer style={{ height: 'calc(100% - 40px)' }}>
            <TenorGIFPicker onGifSelected={handleTenorGifSelected} />
          </ContentContainer>
        </>
      )}
      {paneScreen.screen === 'iframe' && (
        <>
          <BackButtonRowContainer
            onClick={() => handleSelectPaneScreen({ screen: 'default' })}
          >
            <IconWrapper height="30px" width="30px" color={theme.colors.black}>
              <ArrowLeftSvg />
            </IconWrapper>
            <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
              <Title $fontSize={20} style={{ width: 'fit-content', marginRight: 10 }}>
                App smash:
              </Title>
              <IconWrapper height="30px" width="30px">
                {
                  appSmashes.find(app => paneScreen.screenData?.iframeType === app.id)
                    ?.icon
                }
              </IconWrapper>
            </div>
          </BackButtonRowContainer>
          <ContentContainer style={{ height: 'calc(100% - 40px)' }}>
            <AppSmashPicker
              round={slide}
              selectedElement={selectedElement}
              onElementsChange={onElementsChange}
              onRoundChange={onRoundChange}
              iframeType={paneScreen.screenData?.iframeType}
            />
          </ContentContainer>
        </>
      )}
      {paneScreen.screen === 'upload' && (
        <>
          <BackButtonRowContainer onClick={handleCancelUpload}>
            <IconWrapper height="30px" width="30px" color={theme.colors.black}>
              <ArrowLeftSvg />
            </IconWrapper>
            <Title $fontSize={20}>{t('Upload')}</Title>
          </BackButtonRowContainer>
          <ContentContainer style={{ padding: '0px 20px 20px 20px' }}>
            <UploadFilePicker
              round={slide}
              onRoundChange={onRoundChange}
              isBackgroundMedia={false}
              selectedElement={selectedElement}
              droppedImageData={localParentDroppedImageData}
              onElementsChange={onElementsChange}
            />
          </ContentContainer>
        </>
      )}
    </div>
  );
};

const OverviewSelectorContainer = styled.div`
  padding: 0px 20px;
`;

const ContentContainer = styled.div`
  height: calc(100% - 50px);
  overflow-y: auto;
`;

const BackButtonRowContainer = styled.div`
  display: flex;
  height: 30px;
  align-items: center;
  cursor: pointer;
  margin-bottom: 10px;
  padding: 0px 20px;
`;

export default MediaRootTab;

const BackgroundTabContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 0 0 100%;
  gap: 30px;
  width: 100%;
`;

const Title = styled.h3<{ $fontSize: number }>`
  text-align: left;
  width: 100%;
  font-weight: bold;
  font-size: ${({ $fontSize }) => $fontSize}px;
  line-height: 140%;
  color: ${theme.colors.greyVeryDark};
  display: flex;
  align-items: center;
  margin-bottom: 0px;

  @media (max-width: 690px) {
    font-size: ${({ $fontSize }) => $fontSize - 5}px;
  }
`;
